/**
 * Declares each external integration and its capability. The QuickManage write
 * capability is the biggest open architectural variable (docs/ARCHITECTURE.md
 * "Biggest open assumption"): until confirmed, the app treats it as read-only
 * and stores only BB-owned overlay data such as notes, flags, and ownership.
 *
 * `mode` is overridden at runtime from server config once tokens exist; these
 * are the planning defaults.
 */
import { invokeFunction } from "@/lib/api";

export type IntegrationMode = "not_configured" | "read_only" | "read_write";

export interface IntegrationInfo {
  key: "samsara" | "fleetio" | "quickmanage" | "spreadsheets";
  name: string;
  purpose: string;
  mode: IntegrationMode;
  note?: string;
}

export const integrations: IntegrationInfo[] = [
  {
    key: "samsara",
    name: "Samsara",
    purpose: "Vehicle location / HOS",
    mode: "not_configured",
    note: "Read-only (location, HOS). Token in Supabase Vault.",
  },
  {
    key: "fleetio",
    name: "Fleetio",
    purpose: "Fleet maintenance / assets",
    mode: "not_configured",
    note: "Primary fleet/maintenance source (read-only). Token in Supabase Vault.",
  },
  {
    key: "quickmanage",
    name: "QuickManage",
    purpose: "Loads / dispatch",
    mode: "not_configured",
    note: "Primary load source. Read-only assumed; BB notes/flags live in our DB overlay.",
  },
  {
    key: "spreadsheets",
    name: "Spreadsheets",
    purpose: "Legacy loads (Google + Excel)",
    mode: "not_configured",
    note: "Secondary load source until standardized into one import path.",
  },
];

export type IntegrationStatusMap = Partial<Record<IntegrationInfo["key"], IntegrationMode>>;

/**
 * Live integration status. The `integrations-status` edge function checks which
 * secrets are actually set server-side (never exposing the values) and returns
 * the real mode per integration. Falls back to the planning defaults above when
 * the backend is not configured (preview mode) or the call fails.
 */
export async function getIntegrationStatus(): Promise<IntegrationInfo[]> {
  try {
    const { statuses } = await invokeFunction<{ statuses: IntegrationStatusMap }>(
      "integrations-status",
      {},
    );
    return integrations.map((i) => ({ ...i, mode: statuses[i.key] ?? i.mode }));
  } catch {
    return integrations;
  }
}
